import React, {
  forwardRef, useEffect, useImperativeHandle, useRef,
} from 'react';
import {
  JW_PLAYER_LIBRARY_URL,
  JW_PLAYER_SCRIPT_URL,
  JW_PLAYER_LICENSE_KEY,
} from 'Api/jwPlayerConfig';

// ─── Library loader (loads the JW script once, shared across all players) ─────
let jwLoadPromise = null;

function loadJWPlayer() {
  if (typeof window !== 'undefined' && window.jwplayer) return Promise.resolve(window.jwplayer);
  const src = JW_PLAYER_LIBRARY_URL || JW_PLAYER_SCRIPT_URL;
  if (!src) return Promise.reject(new Error('JW Player library URL is not configured'));
  if (jwLoadPromise) return jwLoadPromise;

  jwLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      // Self-hosted builds need the license key set explicitly; cloud-hosted
      // libraries already carry it.
      if (JW_PLAYER_LICENSE_KEY && window.jwplayer) window.jwplayer.key = JW_PLAYER_LICENSE_KEY;
      resolve(window.jwplayer);
    };
    script.onerror = () => {
      jwLoadPromise = null;
      reject(new Error('Failed to load JW Player library'));
    };
    document.head.appendChild(script);
  });
  return jwLoadPromise;
}

let jwInstanceCounter = 0;

/**
 * JW Player wrapper that mirrors the HlsPlayer / NativeVideoPlayer surface so it
 * can drop into the viewer playback flow. JW owns the control bar (which is what
 * surfaces the Chromecast + AirPlay cast buttons on the same WiFi), so the page's
 * custom controls are hidden when this is active.
 *
 * Ref API: getCurrentTime(), getDuration(), seekTo(sec), plus castToggle().
 */
const JWPlayer = forwardRef((props, ref) => {
  const {
    url,
    poster,
    playing = true,
    muted = false,
    volume = 1,
    playbackRate = 1,
    style,
    className,
    onProgress,
    onReady,
    onStart,
    onPlay,
    onPause,
    onBuffer,
    onBufferEnd,
    onEnded,
    onError,
  } = props;

  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const readyRef = useRef(false);
  // Stable DOM id — JW's setup() targets an element by id.
  const idRef = useRef(null);
  if (idRef.current === null) {
    jwInstanceCounter += 1;
    idRef.current = `jwplayer-container-${jwInstanceCounter}`;
  }

  // Expose the same imperative surface the page's controls/resume logic call.
  useImperativeHandle(ref, () => ({
    getCurrentTime: () => {
      try { return playerRef.current?.getPosition?.() || 0; } catch (e) { return 0; }
    },
    getDuration: () => {
      try { return playerRef.current?.getDuration?.() || 0; } catch (e) { return 0; }
    },
    seekTo: (seconds) => {
      try { playerRef.current?.seek?.(seconds); } catch (e) { /* noop */ }
    },
    castToggle: () => {
      try { playerRef.current?.castToggle?.(); } catch (e) { /* noop */ }
    },
    getInternalPlayer: () => playerRef.current,
  }), []);

  // Set up / tear down the player when the source changes.
  useEffect(() => {
    let cancelled = false;
    readyRef.current = false;
    if (!url) return undefined;

    loadJWPlayer().then((jwplayer) => {
      if (cancelled || !containerRef.current) return;
      const player = jwplayer(idRef.current);
      playerRef.current = player;

      player.setup({
        file: url,
        image: poster || undefined,
        autostart: playing,
        mute: muted,
        width: '100%',
        aspectratio: '16:9',
        playbackRateControls: true,
        // Enables the Chromecast / AirPlay cast buttons in the control bar.
        // Casting requires the page to be served over HTTPS.
        cast: {},
        stretching: 'uniform',
      });

      player.on('ready', () => {
        readyRef.current = true;
        try {
          player.setVolume(Math.round((volume ?? 1) * 100));
          if (playbackRate && player.setPlaybackRate) player.setPlaybackRate(playbackRate);
        } catch (e) { /* noop */ }
        if (onReady) onReady();
      });
      player.on('firstFrame', () => { if (onStart) onStart(); });
      player.on('play', () => { if (onPlay) onPlay(); });
      player.on('pause', () => { if (onPause) onPause(); });
      player.on('buffer', () => { if (onBuffer) onBuffer(); });
      player.on('bufferChange', (e) => {
        if (e && typeof e.bufferPercent === 'number' && e.bufferPercent >= 100 && onBufferEnd) onBufferEnd();
      });
      player.on('time', (e) => {
        if (!onProgress) return;
        const duration = e?.duration || player.getDuration?.() || 0;
        const playedSeconds = e?.position || 0;
        onProgress({
          playedSeconds,
          played: duration > 0 ? playedSeconds / duration : 0,
          loadedSeconds: duration > 0 ? ((e?.bufferPercent || 0) / 100) * duration : 0,
          loaded: (e?.bufferPercent || 0) / 100,
        });
      });
      player.on('complete', () => { if (onEnded) onEnded(); });
      player.on('error', (e) => { if (onError) onError(e); });
      player.on('setupError', (e) => { if (onError) onError(e); });
    }).catch((e) => { if (!cancelled && onError) onError(e); });

    return () => {
      cancelled = true;
      try {
        if (playerRef.current) {
          playerRef.current.remove();
          playerRef.current = null;
        }
      } catch (e) { /* noop */ }
    };
    // Re-setup only when the source changes; other props are synced below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Sync declarative playback props onto the imperative JW instance.
  useEffect(() => {
    if (!playerRef.current || !readyRef.current) return;
    try {
      const state = playerRef.current.getState?.();
      if (playing && state === 'paused') playerRef.current.play();
      else if (!playing && state === 'playing') playerRef.current.pause();
    } catch (e) { /* noop */ }
  }, [playing]);

  useEffect(() => {
    if (!playerRef.current || !readyRef.current) return;
    try { playerRef.current.setMute(!!muted); } catch (e) { /* noop */ }
  }, [muted]);

  useEffect(() => {
    if (!playerRef.current || !readyRef.current) return;
    try { playerRef.current.setVolume(Math.round((volume ?? 1) * 100)); } catch (e) { /* noop */ }
  }, [volume]);

  useEffect(() => {
    if (!playerRef.current || !readyRef.current || !playerRef.current.setPlaybackRate) return;
    try { playerRef.current.setPlaybackRate(playbackRate); } catch (e) { /* noop */ }
  }, [playbackRate]);

  return (
    <div className={className} style={{ width: '100%', height: '100%', ...style }}>
      <div id={idRef.current} ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
});

JWPlayer.displayName = 'JWPlayer';

export default JWPlayer;
